import { Button, message, Modal, Select, Typography } from 'antd'
import React from 'react'
import debounce from 'src/utils/debounce'
import { getAll, search, updateTracksInsideCategory } from 'src/api/track'

import s from './index.module.scss'
import useForceRender from 'src/utils/ForceRender'

const { Option } = Select
const { Title } = Typography

interface AddingModalProps {
  category: any
  visible: boolean
  onDone: () => void
  onCancel: () => void
}

const AddingModal: React.FC<AddingModalProps> = ({
  category,
  visible,
  onDone,
  onCancel,
}) => {
  const forceRender = useForceRender()
  const selectedTrack = React.useRef<Tracks>(null)
  const selectLoading = React.useRef<boolean>(false)
  const addLoading = React.useRef<boolean>(false)
  const tracks = React.useRef<Tracks[]>([])

  const handleSearch = async (value) => {
    if (value !== '') {
      selectLoading.current = true
      forceRender()
      const { data } = await search({ title: value, size: 10 })
      tracks.current = data
      selectLoading.current = false
      forceRender()
    }
  }

  const resetModal = React.useCallback(() => {
    selectedTrack.current = null
    selectLoading.current = false
    addLoading.current = false
    tracks.current = []
    forceRender()
  }, [])

  const handleSelect = React.useCallback((option) => {
    selectedTrack.current = {
      id: option.value,
      title: option.label,
    }
    forceRender()
  }, [])

  const handleAdd = React.useCallback(async () => {
    if (!selectedTrack.current) {
      message.warn('Select a Track first !')
      return
    }
    addLoading.current = true
    forceRender()
    const trackIds = category?.tracks.map((i) => i.id)
    await updateTracksInsideCategory(category?.id, category?.type, [
      ...trackIds,
      selectedTrack.current.id,
    ])
    message.success(
      `${selectedTrack.current?.title} is added to ${category.type}`
    )
    resetModal()
    onDone && onDone()
  }, [selectedTrack.current, category])

  const handleCancel = React.useCallback(() => {
    resetModal()
    onCancel && onCancel()
  }, [])

  const filteredResults = tracks.current?.filter((item) => {
    return !category?.tracks.find((i) => i.id === item.id)
  })

  return (
    <>
      <Modal
        title={`Add Track to ${category?.type}`}
        visible={visible}
        onCancel={handleCancel}
        footer={[
          <Button
            type="primary"
            key="add"
            onClick={handleAdd}
            loading={addLoading.current}
          >
            Add
          </Button>,
          <Button key="cancel" onClick={handleCancel}>
            Cancel
          </Button>,
        ]}
      >
        <Title level={5}>{category?.tracks?.length} Tracks added</Title>
        <Select
          allowClear
          showSearch
          autoFocus
          filterOption={false}
          onSearch={debounce(handleSearch, 500)}
          onSelect={handleSelect}
          size="large"
          placeholder="Search track"
          labelInValue={true}
          value={
            selectedTrack.current
              ? {
                  label: selectedTrack.current.title,
                  value: selectedTrack.current.id,
                }
              : null
          }
          className={s.searchSelect}
          onClear={resetModal}
          loading={selectLoading.current}
        >
          {filteredResults?.map((item) => (
            <Option key={item.id}>{item.title}</Option>
          ))}
        </Select>
      </Modal>
    </>
  )
}

AddingModal.displayName = 'AddingModal'
export default AddingModal
